import React, { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertPopup, AlertType } from './AlertPopup';

interface AlertConfig {
  type: AlertType;
  message: string;
}

export const useAlertPopup = () => {
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  
  const showAlert = useCallback((type: AlertType, message: string) => {
    setAlertConfig({ type, message });
  }, []);

  const hideAlert = useCallback(() => {
    setAlertConfig(null);
  }, []);

  const AlertComponent = useCallback(() => {
    if (!alertConfig) return null;
    
    return (
      <AlertPopup
        type={alertConfig.type}
        message={alertConfig.message}
        onClose={hideAlert}
      />
    );
  }, [alertConfig, hideAlert]);

  return {
    showAlert,
    hideAlert,
    AlertComponent,
    isVisible: !!alertConfig
  };
};

// Global alert function
let alertDiv: HTMLDivElement | null = null;
let rootInstance: any = null;

const createAlertContainer = () => {
  if (!alertDiv) {
    alertDiv = document.createElement('div');
    alertDiv.id = 'global-alert-container';
    alertDiv.style.zIndex = '30000'; // Extremely high z-index to stay above SmartOTPModal (z-20000)
    alertDiv.style.position = 'fixed'; // Make sure it's fixed positioned
    alertDiv.style.top = '0';
    alertDiv.style.left = '0';
    alertDiv.style.width = '100%';
    alertDiv.style.height = '100%';
    alertDiv.style.pointerEvents = 'none'; // Let clicks pass through the container
    document.body.appendChild(alertDiv);
    rootInstance = createRoot(alertDiv);
  }
  return { alertDiv, rootInstance };
};

export const showGlobalAlert = (
  type: AlertType,
  message: string,
  actionButton?: { text: string, onClick: () => void }
) => {
  const { rootInstance } = createAlertContainer();
  
  const handleClose = () => {
    if (rootInstance) {
      rootInstance.render(null);
    }
  };
  
  // Render the alert popup with SVG icon
  rootInstance.render(
    <AlertPopup
      type={type}
      message={message}
      onClose={handleClose}
      actionButton={actionButton}
    />
  );
};
